import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { AuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { AppRoutingService } from 'app/main/services/app-routing/app-routing.service';
import { ToastService } from '@services/common/toast.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TermsComponent } from 'app/main/apps/components/terms/terms.component';
import { HttpClient } from '@angular/common/http';
import { UrlService } from '@services/datasources/url.service';
import { UserClaims } from 'app/@types/dto/user-claims.model';

interface NewPasswordFormGroup {
    password: FormControl<string>;
    confirmPassword: FormControl<string>;
}

@Component({
    selector: 'app-new-password-required',
    templateUrl: './new-password-required.component.html',
    styleUrls: ['./new-password-required.component.scss'],
})
export class NewPasswordRequiredComponent implements OnInit {
    // Public
    public newPasswordRequiredForm!: FormGroup<NewPasswordFormGroup>;
    public submitted = false;
    public loading = false;
    public error = '';

    private termsRef: DynamicDialogRef | undefined;

    // Private
    private unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     *
     * @param _appRoutingService
     * @param _authenticationService
     * @param _formBuilder
     * @param _router
     * @param _translateService
     */
    constructor(
        private _appRoutingService: AppRoutingService,
        private _authenticationService: AuthenticationService,
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private _translateService: CustomTranslateService,
        private toastService: ToastService,
        private _dialogService: DialogService,
        private http: HttpClient,
        private urlService: UrlService,
    ) {
        if (!this._authenticationService.isNewPasswordRequired()) {
            this._router.navigate(['/']);
        }
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.newPasswordRequiredForm.controls;
    }

    /**
     * On Submit
     */
    onSubmit() {
        this.newPasswordRequiredForm.markAsPristine();
        this.submitted = true;
        // stop here if form is invalid
        if (this.newPasswordRequiredForm.invalid) {
            return;
        }

        // Sign up
        this.loading = true;
        this._authenticationService
            .newPasswordRequired(this.f.password.value)
            .pipe(first())
            .subscribe({
                next: (data) => {
                    if (data && data.locale) {
                        this._translateService.use(data.locale);
                    }
                    this.loading = false;
                    this.toastService.showSuccess({
                        details: 'authentication.newPasswordRequired.message.success',
                    });
                    this.http
                        .get<UserClaims>(this.urlService.getUserClaimsUrl(undefined), { observe: 'response' })
                        .subscribe({
                            next: (uc) => {
                                if (data?.has_accepted_terms === '0' && !this.termsRef) {
                                    this.termsRef = this._dialogService.open(TermsComponent, {
                                        width: '521px',
                                        height: '536px',
                                        closable: true,
                                        styleClass: 'terms-modal',
                                    });
                                    this.termsRef.onClose.subscribe((termsAccepted: boolean) => {
                                        if (termsAccepted) this._appRoutingService.onSignedIn().catch();
                                        else this._authenticationService.logout(true).pipe(first()).subscribe();
                                    });
                                } else {
                                    this._appRoutingService.onSignedIn().catch();
                                }
                            },
                        });
                },
                error: (error) => {
                    this.toastService.showError({ details: error.message });
                    this.loading = false;
                },
            });
    }

    isFormInvalid(): boolean {
        return (
            this.newPasswordRequiredForm.pristine === true ||
            (this.newPasswordRequiredForm.pristine === false &&
                (this.f.password.invalid || this.f.confirmPassword.invalid))
        );
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.newPasswordRequiredForm = this._formBuilder.group(
            {
                password: ['', [Validators.required, CustomValidators.passwordValidator]],
                confirmPassword: ['', [Validators.required]],
            },
            {
                validators: [CustomValidators.mustMatch('password', 'confirmPassword')],
            },
        );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }
}
